import { Bus, Clock, MapPin, MoreVertical, Route as RouteIcon } from 'lucide-react';

import { Badge } from '@/components/ui/Badge';
import { cn } from '@/lib/utils';
import type { TransportRoute } from '@/types/transport';
import { ROUTE_STATUS, ROUTE_TONE } from '../constants';

export interface RouteCardProps {
  route: TransportRoute;
  onOpen?: (route: TransportRoute) => void;
  onMenu?: (route: TransportRoute) => void;
}

/**
 * One route as a card: what it is called, how far it runs, and what runs it.
 *
 * The sketch is not a map. It is the route's shape at a glance — a line with a
 * mark per stop, drawn from the route's own numbers so a long route with many
 * stops looks like one. Deriving it rather than storing a picture means a route
 * that gains a stop redraws itself, and there is no image to keep in step with
 * the record.
 */
export function RouteCard({ route, onOpen, onMenu }: RouteCardProps) {
  const tone = ROUTE_TONE[route.tone];
  const status = ROUTE_STATUS[route.status];

  return (
    <article className="flex flex-col gap-3 rounded-3xl border border-line bg-surface p-4 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <span className={cn('pill text-[12px] font-bold tracking-label', tone.soft)}>
            {route.code}
          </span>
          <h3 className="mt-2 truncate text-base font-bold text-ink">{route.name}</h3>
          <Badge tone={status.tone} withDot className="mt-2">
            {status.label}
          </Badge>
        </div>

        <RouteSketch stops={route.stops} tone={route.tone} />
      </div>

      <dl className="grid grid-cols-3 gap-1.5 sm:gap-2 border-t border-line pt-3">
        <Figure icon={MapPin} label="Distance" value={`${route.distanceKm} km`} />
        <Figure icon={Clock} label="Est. time" value={`${route.durationMinutes} min`} />
        <Figure icon={RouteIcon} label="Stops" value={String(route.stops)} />
      </dl>

      <footer className="flex items-center justify-between gap-2 border-t border-line pt-3">
        <button
          type="button"
          onClick={() => onOpen?.(route)}
          className="inline-flex min-w-0 items-center gap-2 rounded-xl px-1.5 py-1 text-sm font-semibold text-ink-2 transition-colors hover:text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          <Bus className="h-4 w-4 shrink-0 text-ink-3" aria-hidden />
          <span className="truncate">{route.vehicleNumber ?? 'No vehicle yet'}</span>
        </button>

        <button
          type="button"
          onClick={() => onMenu?.(route)}
          title={`More for ${route.name}`}
          aria-label={`More for ${route.name}`}
          className="grid h-8 w-8 shrink-0 place-items-center rounded-xl text-ink-3 transition-colors hover:bg-surface-2 hover:text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          <MoreVertical className="h-4 w-4" />
        </button>
      </footer>
    </article>
  );
}

/** One of the three numbers under the heading. */
function Figure({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof MapPin;
  label: string;
  value: string;
}) {
  return (
    <div className="min-w-0">
      <dt className="flex items-center gap-1 text-[10.5px] sm:text-[11px] font-bold uppercase tracking-tight text-ink-3">
        <Icon className="h-3 w-3 shrink-0" aria-hidden />
        <span className="truncate">{label}</span>
      </dt>
      <dd className="mt-0.5 truncate text-sm font-bold text-ink">{value}</dd>
    </div>
  );
}

/**
 * The route's shape: a line climbing across the corner with a mark per stop.
 *
 * Capped at seven marks — beyond that they merge into the line and stop saying
 * anything, and the exact count is printed under the heading anyway.
 */
function RouteSketch({ stops, tone }: { stops: number; tone: TransportRoute['tone'] }) {
  const marks = Math.max(2, Math.min(stops, 7));
  const points = Array.from({ length: marks }, (_, index) => {
    const t = index / (marks - 1);
    // A gentle S rather than a straight line, so it reads as a road.
    return {
      x: 6 + t * 60,
      y: 46 - t * 40 + Math.sin(t * Math.PI) * 6,
    };
  });

  return (
    <svg
      viewBox="0 0 72 52"
      className="h-[52px] w-[72px] shrink-0"
      width="72"
      height="52"
      role="img"
      aria-label={`${stops} stops`}
    >
      <polyline
        points={points.map((point) => `${point.x},${point.y}`).join(' ')}
        fill="none"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={ROUTE_TONE[tone].stroke}
      />
      {points.map((point, index) => (
        <circle
          key={index}
          cx={point.x}
          cy={point.y}
          r={index === 0 || index === points.length - 1 ? 3 : 2}
          className={cn(ROUTE_TONE[tone].stroke, 'fill-surface')}
          strokeWidth="2"
        />
      ))}
    </svg>
  );
}
